import { Component, OnDestroy, OnInit, AfterViewInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { AbstractControl, UntypedFormGroup } from '@angular/forms';
import { ContextService, LayoutInterface, OptionDetail, RoleInterface, ServerServiceConfig } from '../context.service';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { BehaviorSubject, Subscription } from 'rxjs';

export interface LoginInfo {
  user: string;
  pwd: string;
  message: string;
}

@Component({
  selector: 'mutara-login',
  templateUrl: './login.component.html',
  standalone: false
})

export class LoginComponent implements OnInit, AfterViewInit, OnDestroy {

  form: UntypedFormGroup = new UntypedFormGroup({});
  model: any = {};
  options: FormlyFormOptions = {
    formState: {
      hidePassword: true,
    },
  };
  wasInvalid: boolean = false;
  isVertical: boolean = false;
  layout: LayoutInterface | undefined;
  isResetting = false;
  activity: string = 'Sign In';
  passwordRegEx: RegExp = /(?=.{8,})((?=.*\d)(?=.*[a-z])(?=.*[A-Z])|(?=.*\d)(?=.*[a-zA-Z])(?=.*[\W_])|(?=.*[a-z])(?=.*[A-Z])(?=.*[\W_])).*/;
  passwordHint: string = 'Password rules: minmum 8 characters, at least 3 of the following; lowercase, uppercase, numbers, symbols';
  subscriptions: Subscription[] = [];
  roleArray: OptionDetail[] = [];
  userfld: FormlyFieldConfig | undefined;

  public roleOptionData = new BehaviorSubject<any[]>([]);

  fields: FormlyFieldConfig[] = [
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          key: 'user', type: 'input',
          className: 'flex-1',
          modelOptions: {
            debounce: { default: 300 },
          },
          validators: {
            patterns: {
              expression: (c: AbstractControl) => /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|\"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*\")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/i.test(c.value),
              message: () => 'An email address requires an internet domain'
            }
          },
          props: {
            label: 'Email',
            placeholder: 'me@email.com',
            required: true,
          }
        },
        {
          key: 'pwd', type: 'input',
          className: 'flex-1',
          expressions: {
            "props.label": (field: FormlyFieldConfig) => (field.options?.formState.hidePassword ? 'Password' : ' Choose New Password')
          },
          props: {
            type: 'password',
            hidePassword: true,
            placeholder: 'bubbl3Gum!',
            required: true,
            // addonRight: {
            //   icon: 'visibility_off',
            //   ariaLabel: 'Show password',
            //   onClick: (props, field, $event) => {
            //     props.hidePassword = !props.hidePassword;
            //     props.type = props.hidePassword ? 'password' : 'text';
            //     props.addonRight.ariaLabel = props.hidePassword ? 'Show password' : 'Mask password';
            //     props.addonRight.icon = props.hidePassword ? 'visibility_off' : 'visibility';
            //   }
            // },
          },
        },
        {
          key: 'forgotten', type: 'input',
          className: 'flex-1',
          expressions: {
            'hide': (field: FormlyFieldConfig) => field.options?.formState.hidePassword
          },
          validators: {
            matches: {
              expression: (c: AbstractControl) => (c.value == c.root.value?.pwd),
              message: () => 'Passwords do not match'
            },
            patterns: {
              expression: (c: AbstractControl) => this.passwordRegEx.test(c.value),
              message: () => this.passwordHint
            }
          },
          props: {
            type: 'password',
            label: 'Confirm New Password',
            required: true,
            hidePassword: true,
            ariaLabel: 'Show password',
            // addonRight: {
            //   icon: 'visibility_off',
            //   onClick: (props, field, $event) => {
            //     props.hidePassword = !props.hidePassword;
            //     props.type = props.hidePassword ? 'password' : 'text';
            //     props.addonRight.ariaLabel = props.hidePassword ? 'Show password' : 'Mask password';
            //     props.addonRight.icon = props.hidePassword ? 'visibility_off' : 'visibility';
            //   }
            // },
          },
        },
        {
          key: 'layout', type: 'select',
          className: 'flex-2',
          expressions: {
            hide: (field: FormlyFieldConfig) => {
              let hideField = ((this.model?.admin ?? 0) == 0 || this.isVertical || (<any[]>field.props?.options ?? [] ).length == 0);
              if (hideField && field.props && (<any[]>field.props?.options ?? [] ).length > 0) {
                this.model.admin = 0;
                field.props.options = [];
              }
              return hideField || !field.options?.formState.hidePassword;
            }
          },
          defaultValue: 'Apply to ${institution}',
          props: {
            label: 'Activity',
          }
        },]
    },
    {
      key: 'descriptive',
      type: 'alert',
      expressions: {
        hide: (field: FormlyFieldConfig) => {
          return !(!!this.isResetting || this.wasInvalid || (!this.options.formState.hidePassword && this.form.valid))
        },
        'props.statusType': (field: FormlyFieldConfig) => {
          let type;
          if (field.props) {
            if (!!this.wasInvalid) {
              type = 'invalid';
              field.props.description = 'Invalid User/Password combination. Please re-enter your password or press **Cancel** and register for an account.';
            } else if (!this.options.formState.hidePassword && this.form.valid) {
              type = 'info';
              field.props.secondaryText = '**Watch for an email or text message to verify the password change.**';
            } else if (!!this.isResetting) {
              type = 'neutral';
              field.props.description = 'After submitting the password change request, please check your email and follow the link to activate the new password. The link will only be available for 24 hours.';
            }
          }
          return type;
        },
      },
    },
    {
      key: 'buttonList', wrappers: ['button-row'],
      props: {
        align: 'end',
      },
      fieldGroup: [
        {
          key: 'forgot', type: 'button',
          expressions: {
            'props.disabled': () => (!!this.isResetting || ('' == (this.model.user ?? '') || (this.userfld?.formControl?.invalid ?? true))),
            hide: () => !!this.isResetting
          },
          props: {
            type: 'button',
            label: 'Forgot Password',
            color: 'accent',
            onClick: () => {
              if ((!this.isResetting && ('' != (this.model.user ?? '')))) {
                this.isResetting = true;
                this.wasInvalid = false;
                this.options.formState.hidePassword = !this.options.formState.hidePassword;
                if (!this.options.formState.hidePassword) {
                  //this.model.forgotten = null;
                  this.form.patchValue({ forgotten: null });
                  this.activity = 'Reset Password';
                } else {
                  this.activity = 'Sign In';
                }
              }
            }
          }
        },
        {
          key: 'cancel', type: 'button',
          props: {
            type: 'button',
            label: 'Cancel',
            color: 'accent',
            onClick: () => {
              this.isResetting = false;
              this.wasInvalid = false;
              this.close();
            }
          }
        },
        {
          key: 'login', type: 'button',
          expressions: {
            'props.disabled': (field: FormlyFieldConfig) => !!this.form.invalid,
            'props.label': (field: FormlyFieldConfig) => field.options?.formState.hidePassword ? 'Login' : 'Request Password Change',
          },
          props: {
            type: 'submit',
            label: 'Login',
            color: 'accent'

          }
        }
      ]
    },
  ];
  /**
   * Class implementation
   * @param router 
   * @param contextSettings 
   * @param elementRef 
   * @param dialogRef 
   */
  constructor(
    private contextSettings: ContextService,
    private dialogRef: MatDialogRef<LoginComponent>
  ) {
    this.subscriptions.push(this.contextSettings.subscribeTo("orientation", (isVertical: boolean) => {
      this.isVertical = isVertical;
    }));
    this.subscriptions.push(this.contextSettings.subscribeTo("layoutIdentity", (layout: LayoutInterface) => {
      this.layout = layout;
    }));
    this.subscriptions.push(this.contextSettings.subscribeTo("logout", (clearSession: boolean) => {
      this.layout = undefined;
      this.model.admin = 0;
      this.roleOptionData.next([]);
    }));
  }

  ngOnInit() {
    this.userfld = this.fields[0]?.fieldGroup?.[0];

    this.subscriptions.push(this.contextSettings.subscribeTo("applicationInfo", (config: ServerServiceConfig) => {
      if (config.passwordRegEx) {
        this.passwordRegEx = new RegExp(config.passwordRegEx);
      }
      if (config.passwordHint) {
        this.passwordHint = config.passwordHint;
      }
      let layoutSelect = this.layoutSelect;
      if (layoutSelect) {
        this.roleArray = config.lookups?.Roles?.sort((a, b) => a.label.localeCompare(b.label)).map((element: any) => {
          let values = element.value.split('|');
          return { id: element.id, category: '', value: values[0], label: element.label, icon: values.length > 1 ? values[1] : null };
        }) ?? [];
        this.model.admin = 0;
        this.userfld?.formControl?.setValue('');
        this.roleOptionData.next([...this.roleArray]);
      }
    }));
  }
  ngAfterViewInit(): void {
    if (this.userfld?.props) {
      this.userfld.formControl?.valueChanges.subscribe({ next: (value) => {
        this.model.admin = 0;
        this.contextSettings.isAdminEmail(this.userfld?.formControl && this.userfld?.formControl.valid ? value : null);
      }});
    }
    this.subscriptions.push(this.roleOptionData.subscribe((newList: OptionDetail[]) => {
      // This can stay until next time since this is a one and done scenario
      this.layoutSelect!.props!.options = newList;
      if (newList.length == 0) {
        this.model.admin = 0;
      }

    }));
    this.subscriptions.push(this.contextSettings.subscribeTo("failedLogin", (failed: boolean) => {
      this.wasInvalid = failed;
    }));

    // Only track if the result is an admin role or not, use is later
    this.subscriptions.push(this.contextSettings.subscribeTo("adminEmail", (result: RoleInterface) => {
      const was = this.model.admin ?? 0;
      if (!!result) {
        this.model.admin = result.role;
        if (!!result.error) {
          this.contextSettings.changeMessage(result.error, 'Acknowledge');
        }
      } else {
        this.model.admin = 0;
      }
      if (was != this.model.admin) {
        this.roleOptionData.next(this.model.admin > 0 ? [...this.roleArray] : []);
      }
    }));


  }
  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.subscriptions = [];
    this.userfld?.formControl?.setValue(null);
  }

  get layoutSelect(): FormlyFieldConfig | undefined {
    return this.fields[0].fieldGroup?.find(defn => defn.key === 'layout');
  }

  getLayouts(): any[] {
    return <any[]>this.layoutSelect?.props?.options;
  }
  save() {
    this.model.defaultLayout = this.layout;
    this.dialogRef.close({ ...this.model });
    this.form.reset();
    this.contextSettings.isAdminEmail('');
  }

  close() {
    this.contextSettings.isAdminEmail('');
    this.roleOptionData.next([]);
    this.userfld?.formControl?.setValue(null);
    this.model.user = '';
    this.activity = 'Sign In';
    this.form.reset();
    this.dialogRef.close(null);
  }
}
